import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'baseline-output' | 'calibration' | 'integrated-output';
export function QuickSelfDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(!isAuthenticated);
  const [hasBaseline] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'baseline-output' : 'baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  // Default values: January 15, 1990 (age ~34, typical founder/creator age)
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  const [calibrationData, setCalibrationData] = useState({
    activationLevel: '',
    triggerContext: [] as string[],
    awarenessMode: '',
    optionalContext: ''
  });
  const baselineOutput = {
    coreOrientation: 'You are wired to observe and stabilize systems before fully entering them. When complexity rises, you move toward clarity — understanding patterns, naming dynamics, and organizing what feels scattered.',
    primaryTension: 'At the core of this system is a recurring pull between control and surrender — between architecting reality and letting experience move you without managing it.',
    structuralBlindSpot: 'Clarity can sometimes feel like completion. Understanding a pattern may arrive faster than emotional or bodily integration.'
  };
  const integratedOutput = {
    currentExpression: 'Right now, this pattern is running at high intensity, primarily activated by work pressure and internal self-expectation. You are aware of it in the moment, which creates space for recalibration rather than automatic response.',
    tensionSnapshot: 'Control seeking clarity while pressure demands immediate action.',
    whatChanged: {
      activationLevel: {
        before: 'Moderate (baseline assumption)',
        after: 'High (your input)',
        why: 'Higher activation means the pattern is more automatic and harder to interrupt'
      },
      triggerContext: {
        before: 'General life contexts',
        after: 'Work pressure + Internal self-expectation',
        why: 'Knowing where it activates helps you recognize the pattern earlier'
      },
      awarenessMode: {
        before: 'Aware only after the fact (baseline tendency)',
        after: 'Clearly aware of it (your current state)',
        why: 'Real-time awareness creates the possibility of choosing a different response'
      }
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    if ((baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating your baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      // Check if this baseline collection is for a paid report
      const returnTo = localStorage.getItem('baselineReturnTo');
      if (returnTo === 'paid-report-inputs') {
        // Clear the flag and route to paid report inputs
        localStorage.removeItem('baselineReturnTo');
        navigate('/paid-report-inputs');
      } else {
        // Normal free flow - continue to baseline output
        setCurrentPhase('baseline-output');
      }
    }, 2000);
  };
  const handleCalibrationSubmit = () => {
    if (!calibrationData.activationLevel || calibrationData.triggerContext.length === 0 || !calibrationData.awarenessMode) return;
    setIsGenerating(true);
    setGeneratingMessage('Refining your snapshot...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('integrated-output');
    }, 2000);
  };
  const handleTriggerToggle = (trigger: string) => {
    setCalibrationData(prev => ({
      ...prev,
      triggerContext: prev.triggerContext.includes(trigger) ? prev.triggerContext.filter(t => t !== trigger) : [...prev.triggerContext, trigger]
    }));
  };
  if (showLoginModal) {
    return <>
        <Helmet data-id="element-2278">
          <title data-id="element-2279">Quick Self Decode — Login Required | Lazy Monk Labs</title>
        </Helmet>
        <div className="fixed inset-0 bg-[#0A0A0A]/95 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-id="element-2280">
          <div className="max-w-md w-full bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-2281">
            <h2 className="text-2xl font-medium mb-4" data-id="element-2282">Save your snapshot</h2>
            <p className="text-[#F5F5F5]/70 mb-8 leading-relaxed" data-id="element-2283">
              Save your snapshot and return to it anytime.
            </p>
            <button onClick={() => setShowLoginModal(false)} className="w-full px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors" data-id="element-2284">
              Continue with Google
            </button>
            <button onClick={() => navigate('/decode')} className="w-full mt-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors" data-id="element-2285">
              Cancel
            </button>
          </div>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-2286">
          <title data-id="element-2287">Generating Snapshot | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-2288">
          <div className="text-center" data-id="element-2289">
            <div className="mb-6" data-id="element-2290">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-2291"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-2292">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-2293">
        <title data-id="element-2294">Quick Self Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2295">
        <header data-id="element-2296">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2297">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2298">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2299">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2300" />
                <span className="text-lg font-medium tracking-tight" data-id="element-2301">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2302">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>
        <main className="pt-24 pb-16 px-6" data-id="element-2303">
          <div className="max-w-2xl mx-auto" data-id="element-2304">
            {currentPhase === 'baseline-collection' && <div data-id="element-2305">
                <div className="mb-12" data-id="element-2306">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2307">
                    🧭 Quick Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2308">
                    Structural baseline
                  </p>
                </div>
                <div className="space-y-8 mb-12" data-id="element-2309">
                  <div data-id="element-2310">
                    <label className="block text-sm font-medium mb-3" data-id="element-2311">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-2312">
                        <div className="text-2xl font-medium" data-id="element-2313">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-2314">
                      <div data-id="element-2315">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2316">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2317">
                          <option value="" data-id="element-2318">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-2319">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2320">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2321">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-2322">
                          <option value="" data-id="element-2323">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-2324">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2325">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2326">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2327">
                          <option value="" data-id="element-2328">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-2329">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-2330">
                    <label className="block text-sm font-medium mb-3" data-id="element-2331">
                      Time of birth{' '}
                      <span className="text-[#F5F5F5]/40" data-id="element-2332">(optional)</span>
                    </label>
                    <div className="space-y-3 mb-4" data-id="element-2333">
                      {[{
                    value: 'exact',
                    label: 'I know the exact time'
                  }, {
                    value: 'approximate',
                    label: 'I know approximately'
                  }, {
                    value: 'not-sure',
                    label: "Not sure / Don't know"
                  }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${baselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2334">
                          <input type="radio" name="timeKnown" value={option.value} checked={baselineData.timeKnown === option.value} onChange={e => setBaselineData(prev => ({
                      ...prev,
                      timeKnown: e.target.value,
                      timeOfBirth: '',
                      hour: '12',
                      minute: '00',
                      period: 'AM'
                    }))} className="sr-only" data-id="element-2335" />
                          <span className="text-sm" data-id="element-2336">{option.label}</span>
                        </label>)}
                    </div>
                    {(baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-2337">
                        <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-2338">
                          <div className="text-center mb-8" data-id="element-2339">
                            <div className="text-4xl font-medium tracking-tight" data-id="element-2340">
                              {baselineData.hour}:{baselineData.minute}{' '}
                              {baselineData.period}
                            </div>
                            <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-2341">
                              {baselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                            </p>
                          </div>
                          <div className="mb-6" data-id="element-2342">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2343">
                              Hour
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-2344">
                              {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateTimeOfBirth(h, baselineData.minute, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2345">
                                  {h}
                                </button>)}
                            </div>
                          </div>
                          <div className="mb-6" data-id="element-2346">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2347">
                              Minute
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-2348">
                              {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, m, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2349">
                                  {m}
                                </button>)}
                            </div>
                            <div className="mt-2" data-id="element-2350">
                              <input type="number" min="0" max="59" value={baselineData.minute} onChange={e => {
                          const val = e.target.value.padStart(2, '0');
                          if (parseInt(val) >= 0 && parseInt(val) <= 59) updateTimeOfBirth(baselineData.hour, val, baselineData.period);
                        }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2351" />
                            </div>
                          </div>
                          <div data-id="element-2352">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2353">
                              Period
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-2354">
                              {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, baselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${baselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2355">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                  <div data-id="element-2356">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-2357">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2358" />
                  </div>
                  <div data-id="element-2359">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-2360">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2361" />
                  </div>
                </div>
                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country || (baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2362">
                  Generate Snapshot
                </button>
              </div>}

            {currentPhase === 'baseline-output' && <div data-id="element-2363">
                <div className="mb-12" data-id="element-2364">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2365">
                    🧭 Quick Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2366">
                    Structural baseline
                  </p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2367">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2368">
                    <span className="text-2xl" data-id="element-2369">🧠</span>
                    <div className="flex-1" data-id="element-2370">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2371">
                        How your system is wired
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2372">
                        {baselineOutput.coreOrientation}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2373">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2374">
                    <span className="text-2xl" data-id="element-2375">⚖️</span>
                    <div className="flex-1" data-id="element-2376">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2377">
                        The tension that shapes your life
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2378">
                        {baselineOutput.primaryTension}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2379">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2380">
                    <span className="text-2xl" data-id="element-2381">⚠️</span>
                    <div className="flex-1" data-id="element-2382">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2383">
                        Where this system can mislead itself
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2384">
                        {baselineOutput.structuralBlindSpot}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 text-center" data-id="element-2385">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-2386">
                    Structural baseline — this does not change over time
                  </p>
                </div>
                <div className="mb-12" data-id="element-2387">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-2388">
                    <span data-id="element-2389">📘</span>
                    <span data-id="element-2390">How was this generated?</span>
                    <span className="text-xs" data-id="element-2391">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2392">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2393">
                        This snapshot is generated from your baseline structure
                        — the parts of you that don't change with mood or
                        circumstance. Birth data acts as coordinates to map how
                        this system processes information and stabilizes
                        tension. Questions don't create insight. They tune how
                        this structure is expressing right now.
                      </p>
                    </div>}
                </div>
                <div className="space-y-4" data-id="element-2394">
                  <button onClick={() => setCurrentPhase('calibration')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2395">
                    🧩 Refine this snapshot
                  </button>
                  <div className="text-center" data-id="element-2396">
                    <p className="text-xs text-[#F5F5F5]/50 mb-2" data-id="element-2397">
                      Optional · ~30 seconds
                    </p>
                    <p className="text-xs text-[#F5F5F5]/40 leading-relaxed max-w-md mx-auto" data-id="element-2398">
                      This doesn't change who you are — only how this pattern is
                      expressing right now.
                    </p>
                  </div>
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-2399">
                    Skip for now
                  </button>
                </div>
              </div>}

            {currentPhase === 'calibration' && <div data-id="element-2400">
                <div className="mb-12" data-id="element-2401">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2402">
                    Refine your snapshot
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2403">~30 seconds</p>
                </div>
                <div className="space-y-12 mb-12" data-id="element-2404">
                  <div data-id="element-2405">
                    <h2 className="text-lg font-medium mb-6" data-id="element-2406">
                      How intense does your inner pressure feel right now?
                    </h2>
                    <div className="space-y-3" data-id="element-2407">
                      {['Low', 'Moderate', 'High', 'Overwhelming'].map(level => <label key={level} className={`block border p-5 cursor-pointer transition-colors ${calibrationData.activationLevel === level ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2408">
                            <input type="radio" name="activationLevel" value={level} checked={calibrationData.activationLevel === level} onChange={e => setCalibrationData(prev => ({
                      ...prev,
                      activationLevel: e.target.value
                    }))} className="sr-only" data-id="element-2409" />
                            <span className="text-base" data-id="element-2410">{level}</span>
                          </label>)}
                    </div>
                  </div>
                  <div data-id="element-2411">
                    <h2 className="text-lg font-medium mb-3" data-id="element-2412">
                      Where does this show up the most lately?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-2413">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-2414">
                      {['Work / responsibility', 'Close relationships', 'Uncertainty / transition', 'Internal self-expectation'].map(trigger => <label key={trigger} className={`block border p-5 cursor-pointer transition-colors ${calibrationData.triggerContext.includes(trigger) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2415">
                          <input type="checkbox" value={trigger} checked={calibrationData.triggerContext.includes(trigger)} onChange={() => handleTriggerToggle(trigger)} className="sr-only" data-id="element-2416" />
                          <span className="text-base" data-id="element-2417">{trigger}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-2418">
                    <h2 className="text-lg font-medium mb-6" data-id="element-2419">
                      When this pattern activates, you're usually…
                    </h2>
                    <div className="space-y-3" data-id="element-2420">
                      {['Clearly aware of it', 'Aware only after the fact', 'Mostly run by it'].map(mode => <label key={mode} className={`block border p-5 cursor-pointer transition-colors ${calibrationData.awarenessMode === mode ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2421">
                          <input type="radio" name="awarenessMode" value={mode} checked={calibrationData.awarenessMode === mode} onChange={e => setCalibrationData(prev => ({
                      ...prev,
                      awarenessMode: e.target.value
                    }))} className="sr-only" data-id="element-2422" />
                          <span className="text-base" data-id="element-2423">{mode}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-2424">
                    <h2 className="text-lg font-medium mb-3" data-id="element-2425">
                      Anything you want this snapshot to factor in? (Optional)
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-2426">
                      A situation, relationship, or theme you're currently
                      navigating.
                    </p>
                    <textarea value={calibrationData.optionalContext} onChange={e => setCalibrationData(prev => ({
                  ...prev,
                  optionalContext: e.target.value
                }))} rows={3} maxLength={200} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional context..." data-id="element-2427" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-2428">
                      {calibrationData.optionalContext.length}/200
                    </p>
                  </div>
                </div>
                <button onClick={handleCalibrationSubmit} disabled={!calibrationData.activationLevel || calibrationData.triggerContext.length === 0 || !calibrationData.awarenessMode} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2429">
                  Apply Refinement
                </button>
              </div>}

            {currentPhase === 'integrated-output' && <div data-id="element-2430">
                <div className="mb-12" data-id="element-2431">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2432">
                    🧭 Quick Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2433">Refined Snapshot</p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2434">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2435">
                    <span className="text-2xl" data-id="element-2436">🧱</span>
                    <div className="flex-1" data-id="element-2437">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-2438">
                        Unchanged structural layer
                      </p>
                      <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-2439">
                        You are wired to observe and stabilize systems before
                        fully entering them. The core tension: control vs
                        surrender.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2440">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2441">
                    <span className="text-2xl" data-id="element-2442">🔄</span>
                    <div className="flex-1" data-id="element-2443">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-2444">
                        Current expression (based on your inputs)
                      </p>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2445">
                        {integratedOutput.currentExpression}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2446">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2447">
                    <span className="text-2xl" data-id="element-2448">🎯</span>
                    <div className="flex-1" data-id="element-2449">
                      <p className="text-lg font-medium text-[#F5F5F5]/90 italic" data-id="element-2450">
                        "{integratedOutput.tensionSnapshot}"
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2451">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-2452">
                    <span data-id="element-2453">🔍</span>
                    <span data-id="element-2454">What changed after refinement</span>
                    <span className="text-xs" data-id="element-2455">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-2456">
                      {Object.entries(integratedOutput.whatChanged).map(([key, value]) => <div key={key} className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2457">
                            <h3 className="text-sm font-medium mb-4 capitalize" data-id="element-2458">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </h3>
                            <div className="space-y-3 text-sm" data-id="element-2459">
                              <div data-id="element-2460">
                                <span className="text-[#F5F5F5]/50" data-id="element-2461">
                                  Before:{' '}
                                </span>
                                <span className="text-[#F5F5F5]/70" data-id="element-2462">
                                  {value.before}
                                </span>
                              </div>
                              <div data-id="element-2463">
                                <span className="text-[#F5F5F5]/50" data-id="element-2464">
                                  After:{' '}
                                </span>
                                <span className="text-[#F5F5F5]/70" data-id="element-2465">
                                  {value.after}
                                </span>
                              </div>
                              <div className="pt-2 border-t border-[#2B2B2B]" data-id="element-2466">
                                <span className="text-[#F5F5F5]/50" data-id="element-2467">
                                  Why this matters:{' '}
                                </span>
                                <span className="text-[#F5F5F5]/70" data-id="element-2468">
                                  {value.why}
                                </span>
                              </div>
                            </div>
                          </div>)}
                    </div>}
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2469">
                  <h2 className="text-xl font-medium mb-6" data-id="element-2470">
                    What this snapshot does not cover
                  </h2>
                  <div className="space-y-4 text-[#F5F5F5]/70 leading-relaxed" data-id="element-2471">
                    <p data-id="element-2472">
                      This snapshot shows your{' '}
                      <strong className="text-[#F5F5F5]" data-id="element-2473">core structure</strong>{' '}
                      and how it is{' '}
                      <strong className="text-[#F5F5F5]" data-id="element-2474">
                        expressing right now
                      </strong>
                      .
                    </p>
                    <p data-id="element-2475">It does not map:</p>
                    <ul className="space-y-2 ml-4" data-id="element-2476">
                      <li data-id="element-2477">• how this pattern formed</li>
                      <li data-id="element-2478">• how it repeats across life phases</li>
                      <li data-id="element-2479">• how it shows up in decisions and relationships</li>
                      <li data-id="element-2480">• where it creates friction or leverage long-term</li>
                      <li data-id="element-2481">• how multiple patterns interact inside you</li>
                    </ul>
                    <p className="pt-4" data-id="element-2482">
                      Those layers require a deeper diagnostic.
                    </p>
                  </div>
                </div>

                <div className="mb-12" data-id="element-2483">
                  <h2 className="text-xl font-medium mb-6" data-id="element-2484">Deep Self Decode</h2>
                  <div className="space-y-4 text-[#F5F5F5]/70 leading-relaxed mb-8" data-id="element-2485">
                    <p data-id="element-2486">
                      The Deep Self Decode maps this same structure across{' '}
                      <strong className="text-[#F5F5F5]" data-id="element-2487">
                        time, context, and interaction
                      </strong>
                      .
                    </p>
                    <p data-id="element-2488">It shows:</p>
                    <ul className="space-y-2 ml-4" data-id="element-2489">
                      <li data-id="element-2490">• why this pattern exists</li>
                      <li data-id="element-2491">• how it evolved</li>
                      <li data-id="element-2492">• where it repeats</li>
                      <li data-id="element-2493">• what it stabilizes and what it destabilizes</li>
                    </ul>
                    <p className="pt-4" data-id="element-2494">
                      This is not an extension of the snapshot.
                      <br data-id="element-2495" />
                      It is a different layer of diagnosis.
                    </p>
                  </div>

                  <Link to="/report-detail?report=deep-self-decode" className="block w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2496">
                    Explore Deep Self Decode
                  </Link>
                  <p className="text-xs text-[#F5F5F5]/40 text-center mt-3" data-id="element-2497">
                    A full diagnostic of this system
                  </p>
                </div>

                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-2498">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-2499">
                    💾 Save & return later
                  </button>
                  <button onClick={() => {
                setCurrentPhase('baseline-output');
                setCalibrationData({
                  activationLevel: '',
                  triggerContext: [],
                  awarenessMode: '',
                  optionalContext: ''
                });
              }} className="w-full px-8 py-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors" data-id="element-2500">
                    🔁 Run another Quick Decode
                  </button>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}